import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { shareOrigin } from "../lib/api";
import BottomNav from "../components/BottomNav";
import ContactLink from "../components/ContactLink";
import { toast } from "sonner";
import {
  ArrowLeft, Users2, UserPlus, Send, RefreshCw, BookUser, Trash2, Copy, Share2,
} from "lucide-react";

/**
 * Phone Contacts (Universal Contact Integration - Parts 2/3).
 *
 * Import path A: Android Contact Picker API (Chrome PWA only).
 * Import path B: Manual add (name + email/phone).
 *
 * Every row shows one of three statuses:
 *   • 🟢 On Proximity        → tapping opens Dashboard, flies to their marker.
 *   • Invite to Proximity   → tap opens native share (SMS/Email/Any app) with personal link.
 *   • Invitation Sent       → 'Remind' reuses the SAME invite token (no dup rows).
 */
export default function PhoneContacts() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [manual, setManual] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(null); // row.id being invited/removed

  const load = useCallback(async () => {
    try {
      const r = await api.get("/phonebook");
      setRows(r.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    // Poll every 30 s so pending invitations auto-flip to "On Proximity"
    // when the recipient registers, without requiring a manual refresh.
    const t = setInterval(load, 30000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [load]);

  // ---------- Import (Android Contact Picker) ----------
  const importFromPhone = async () => {
    if (!("contacts" in navigator) || !navigator.contacts?.select) {
      toast.message("Contact picker not available", {
        description: "Your browser doesn't expose the phone's address book. Use 'Add manually' below.",
      });
      return;
    }
    setImporting(true);
    try {
      const picked = await navigator.contacts.select(["name", "email", "tel"], { multiple: true });
      if (!picked || !picked.length) { setImporting(false); return; }
      const batch = [];
      for (const p of picked) {
        const name = (p.name || []).join(" ").trim();
        const email = (p.email || [])[0] || null;
        const phone = (p.tel || [])[0] || null;
        if (!email && !phone) continue;
        batch.push({ name: name || email || phone, email, phone });
      }
      if (!batch.length) { toast.message("No contacts had emails or phone numbers"); setImporting(false); return; }
      const r = await api.post("/phonebook/import", { contacts: batch });
      toast.success(`Imported ${r.data.imported} of ${batch.length}`);
      setRows(r.data.rows || []);
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  // ---------- Manual add ----------
  const addManual = async (e) => {
    e.preventDefault();
    const name = manual.name.trim();
    const email = manual.email.trim() || null;
    const phone = manual.phone.trim() || null;
    if (!name && !email && !phone) return;
    if (!email && !phone) { toast.error("Need at least an email or a phone"); return; }
    try {
      const r = await api.post("/phonebook/import", { contacts: [{ name: name || email || phone, email, phone }] });
      setRows(r.data.rows || []);
      setManual({ name: "", email: "", phone: "" });
      toast.success("Added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add");
    }
  };

  // ---------- Invite / Share ----------
  const inviteRow = async (row) => {
    setBusy(row.id);
    try {
      let token = row.invite_token;
      if (!token) {
        const r = await api.post(`/phonebook/${row.id}/invite`);
        token = r.data.token;
      }
      const url = `${shareOrigin()}/join/${token}`;
      const text = `Hey — join me on Proximity so we can share locations when we're near each other. `;

      // Prefer native Web Share (Android + iOS PWAs surface Messages, Mail, WhatsApp, etc.)
      if (navigator.share) {
        try {
          await navigator.share({ title: "Join me on Proximity", text, url });
        } catch { /* user cancelled */ }
      } else {
        // Fallback: try SMS if phone is on file, else mail.
        if (row.phone_masked) {
          window.open(`sms:?&body=${encodeURIComponent(text + url)}`, "_self");
        } else if (row.email_masked) {
          window.open(`mailto:?subject=${encodeURIComponent("Join me on Proximity")}&body=${encodeURIComponent(text + url)}`, "_self");
        } else {
          try { await navigator.clipboard.writeText(url); toast.success("Invite link copied"); }
          catch { toast.error("Copy failed"); }
        }
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    } finally {
      setBusy(null);
    }
  };

  const copyLink = async (row) => {
    let token = row.invite_token;
    if (!token) {
      const r = await api.post(`/phonebook/${row.id}/invite`);
      token = r.data.token;
      await load();
    }
    const url = `${shareOrigin()}/join/${token}`;
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); }
  };

  const removeRow = async (row) => {
    if (!window.confirm(`Remove ${row.name} from Phone Contacts?`)) return;
    try {
      await api.delete(`/phonebook/${row.id}`);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch { toast.error("Failed"); }
  };

  const openOnMap = (row) => {
    if (!row.matched_user_id) return;
    navigate(`/dashboard?focus=${encodeURIComponent(row.matched_user_id)}`);
  };

  const supportsPicker = typeof navigator !== "undefined" && "contacts" in navigator && !!navigator.contacts?.select;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 pb-28">
      <header className="px-6 pt-10 pb-6 flex items-center gap-3">
        <Link to="/contacts" data-testid="back-to-contacts" className="size-9 rounded-full border border-white/10 grid place-items-center">
          <ArrowLeft size={16}/>
        </Link>
        <div>
          <div className="label-eyebrow flex items-center gap-2"><BookUser size={12}/> Universal Contacts</div>
          <h1 className="font-display font-black text-3xl tracking-tighter mt-1">Phone Contacts</h1>
        </div>
      </header>

      {/* Import CTA */}
      <section className="px-6">
        <div className="rounded-2xl p-5 border border-[#007AFF]/30 bg-gradient-to-br from-[#007AFF]/12 to-transparent">
          <div className="label-eyebrow text-[#007AFF]">One tap</div>
          <h2 className="font-display font-black text-2xl tracking-tighter mt-1">Find friends already on Proximity</h2>
          <p className="text-sm text-zinc-400 mt-2">
            {supportsPicker
              ? "Tap 'Import from phone' to pick contacts. Only picked contacts are shared — nothing else is uploaded. We hash emails and phones before storage so raw values never leave your device."
              : "Your browser doesn't expose the address book (iOS Safari & desktop). Use 'Add manually' — we still hash before storing."}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              data-testid="import-phone-btn"
              onClick={importFromPhone}
              disabled={importing || !supportsPicker}
              className="flex-1 min-w-[160px] bg-[#007AFF] hover:bg-[#005ec4] text-white font-bold px-5 py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <UserPlus size={16}/> {importing ? "Importing…" : "Import from phone"}
            </button>
            <button
              data-testid="refresh-btn"
              onClick={load}
              className="bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold px-4 py-3 rounded-full inline-flex items-center gap-2"
              title="Check for new matches"
            >
              <RefreshCw size={14}/>
            </button>
          </div>
        </div>
      </section>

      {/* Manual add */}
      <section className="px-6 mt-6">
        <details className="rounded-2xl border border-white/5 bg-white/[0.02] open:bg-white/[0.04]">
          <summary className="cursor-pointer p-4 list-none flex items-center gap-2 text-sm font-semibold">
            <UserPlus size={14} className="text-zinc-500"/> Add manually
            <span className="ml-auto text-zinc-500 text-xs">▾</span>
          </summary>
          <form onSubmit={addManual} data-testid="manual-add-form" className="p-4 pt-0 space-y-2">
            <input
              data-testid="manual-name"
              value={manual.name}
              onChange={(e) => setManual((m) => ({ ...m, name: e.target.value }))}
              placeholder="Name"
              className="w-full bg-zinc-900 border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#007AFF]"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input
                data-testid="manual-email"
                value={manual.email}
                onChange={(e) => setManual((m) => ({ ...m, email: e.target.value }))}
                type="email" placeholder="email@example.com"
                className="w-full bg-zinc-900 border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#007AFF]"
              />
              <input
                data-testid="manual-phone"
                value={manual.phone}
                onChange={(e) => setManual((m) => ({ ...m, phone: e.target.value }))}
                type="tel" placeholder="+1 555 555 5555"
                className="w-full bg-zinc-900 border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#007AFF]"
              />
            </div>
            <button
              data-testid="manual-add-submit"
              type="submit"
              className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold px-5 py-2.5 rounded-lg"
            >
              Add contact
            </button>
          </form>
        </details>
      </section>

      {/* List */}
      <section className="px-6 mt-6">
        <div className="label-eyebrow mb-3 flex items-center gap-2">
          <Users2 size={12}/> Your phone contacts · {rows.length}
        </div>
        {loading ? (
          <div className="p-8 rounded-2xl border border-white/5 text-center text-zinc-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 rounded-2xl border border-white/5 text-center text-zinc-500">
            No phone contacts yet. Import from your phone or add manually above.
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <PhonebookRow
                key={r.id}
                row={r}
                busy={busy === r.id}
                onOpenOnMap={openOnMap}
                onInvite={inviteRow}
                onCopy={copyLink}
                onRemove={removeRow}
              />
            ))}
          </div>
        )}
      </section>

      <BottomNav />
    </div>
  );
}

function PhonebookRow({ row, busy, onOpenOnMap, onInvite, onCopy, onRemove }) {
  const isOnProximity = row.status === "on_proximity";
  const isInvited = row.status === "invited";

  return (
    <div className="flex items-center gap-3 p-4 rounded-xl border border-white/5 hover:bg-white/[0.03]" data-testid={`phonebook-row-${row.id}`}>
      <div className="size-11 rounded-full bg-zinc-800 overflow-hidden grid place-items-center font-display font-bold shrink-0">
        {row.matched_user?.picture
          ? <img src={row.matched_user.picture} alt="" className="w-full h-full object-cover"/>
          : (row.name || "?").slice(0, 1).toUpperCase()}
      </div>

      <div className="flex-1 min-w-0">
        {/* Name — a ContactLink when they're already on Proximity */}
        {isOnProximity && row.matched_user_id ? (
          <ContactLink
            userId={row.matched_user_id}
            name={row.matched_user?.name || row.name}
            testId={`phonebook-name-${row.id}`}
            className="font-semibold"
          />
        ) : (
          <div className="font-semibold truncate">{row.name}</div>
        )}
        <div className="text-xs text-zinc-500 truncate">
          {row.email_masked || row.phone_masked || ""}
        </div>
      </div>

      {/* Status pill */}
      {isOnProximity ? (
        <button
          data-testid={`open-on-map-${row.id}`}
          onClick={() => onOpenOnMap(row)}
          className="text-[11px] font-bold uppercase tracking-wider text-[#34C759] bg-[#34C759]/10 border border-[#34C759]/30 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5"
        >
          <span className="size-1.5 rounded-full bg-[#34C759]"/> On Proximity
        </button>
      ) : isInvited ? (
        <>
          <span
            data-testid={`invitation-sent-${row.id}`}
            className="text-[10px] font-bold uppercase tracking-wider text-[#FFCC00] bg-[#FFCC00]/10 border border-[#FFCC00]/30 rounded-full px-3 py-1.5"
          >
            Invitation Sent
          </span>
          <button
            data-testid={`remind-${row.id}`}
            onClick={() => onInvite(row)}
            disabled={busy}
            className="size-9 rounded-full border border-white/10 grid place-items-center text-zinc-300 hover:bg-white/5"
            title="Remind (same link, no duplicate)"
          >
            <Send size={14}/>
          </button>
        </>
      ) : (
        <button
          data-testid={`invite-${row.id}`}
          onClick={() => onInvite(row)}
          disabled={busy}
          className="text-[11px] font-bold uppercase tracking-wider bg-[#007AFF] hover:bg-[#005ec4] text-white rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          <Share2 size={12}/> Invite
        </button>
      )}

      <button
        onClick={() => onCopy(row)}
        className="size-9 rounded-full border border-white/10 grid place-items-center text-zinc-400 hover:text-zinc-100"
        title="Copy invite link"
        data-testid={`copy-${row.id}`}
      >
        <Copy size={14}/>
      </button>
      <button
        onClick={() => onRemove(row)}
        className="size-9 rounded-full border border-white/10 grid place-items-center text-zinc-400 hover:text-[#FF3B30] hover:border-[#FF3B30]/40"
        title="Remove"
        data-testid={`remove-${row.id}`}
      >
        <Trash2 size={14}/>
      </button>
    </div>
  );
}
